import { Download } from 'lucide-react';
import { useState } from 'react';
import { Trans } from 'react-i18next';
import { Field, NativeSelect } from '@/components/form';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useMe } from '@/lib/clients';
import { formatDateTime } from '@/lib/format';
import {
  IRN_CANCEL_REASONS,
  irnCancelReasonLabel,
  testConnectionHint,
  useEInvoicePreview,
  useIrnAction,
} from '@/lib/gst';
import { useT } from '@/lib/i18n';
import { isIndia } from '@/lib/org-locale';
import { ltrIn } from '@/lib/sales';
import { cn } from '@/lib/utils';

/**
 * E-invoice (IRP) strip on an approved India invoice or credit note (document 06 T9): the IRN
 * and acknowledgement once registered, else what the IRP rules still find, and the actions
 * Generate IRN, Download JSON and Cancel IRN (within 24 hours). Renders nothing for UAE clients.
 */
export function IrnPanel({ documentId, number }: { documentId: string; number: string | null }) {
  const t = useT();
  const india = isIndia();
  const q = useEInvoicePreview(documentId, india);
  const action = useIrnAction(documentId);
  const me = useMe();
  const [cancelling, setCancelling] = useState(false);
  const [reasonCode, setReasonCode] = useState(2);
  const [reason, setReason] = useState('');
  if (!india || !q.data) return null;
  const { irn, validation } = q.data;
  const cancelled = irn ? null : (q.data.cancelledIrn ?? null);
  const staff = me.data?.isStaff ?? false;
  const within24h = irn ? Date.now() - new Date(irn.ackDate).getTime() < 24 * 3600 * 1000 : false;

  const download = () => {
    const url = URL.createObjectURL(new Blob([q.data.json], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `INV-01_${number ?? documentId}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div
      data-testid="irn-panel"
      className={cn(
        'flex flex-wrap items-center gap-3 rounded-md border px-4 py-2.5 text-sm',
        !irn && !validation.valid ? 'border-[#FCD34D] bg-[#FFFBEB]' : 'bg-card',
      )}
    >
      <span className="font-medium">{t('taxPages.irn.title')}</span>
      {irn?.provider === 'mock' ? (
        <span
          className="rounded-full bg-[#FEF3F2] px-2 py-0.5 text-xs font-medium text-[#B42318]"
          title={testConnectionHint()}
          data-testid="irn-test"
        >
          {t('taxPages.irn.testIrn')}
        </span>
      ) : null}
      {irn ? (
        <span className="min-w-48 flex-1 text-muted-foreground">
          <Trans
            t={t}
            i18nKey="taxPages.irn.generatedLine"
            values={{ ackNo: ltrIn(irn.ackNo), when: formatDateTime(irn.ackDate) }}
            components={{ b: <span className="text-foreground" />, n: <span className="tnum" /> }}
          />
          <span className="block truncate font-mono text-xs" title={irn.irn}>
            {irn.irn}
          </span>
          {t('taxPages.irn.locked')}
        </span>
      ) : cancelled ? (
        <span className="min-w-48 flex-1 text-muted-foreground" data-testid="irn-cancelled">
          <Trans
            t={t}
            i18nKey="taxPages.irn.cancelledLine"
            values={{
              when: formatDateTime(cancelled.cancelledAt),
              reason: cancelled.cancelReason,
            }}
            components={{ b: <span className="text-foreground" /> }}
          />
          <span className="block truncate font-mono text-xs line-through" title={cancelled.irn}>
            {cancelled.irn}
          </span>
          {validation.valid ? t('taxPages.irn.editableAgain') : t('taxPages.irn.fixFirst')}
        </span>
      ) : validation.valid && validation.warnings.length ? (
        <span className="min-w-48 flex-1 text-muted-foreground">
          {t('taxPages.irn.notRegistered')}
          <ul className="list-disc ps-5 text-xs">
            {validation.warnings.map((w) => (
              <li key={w.rule}>
                {w.message} ({w.rule})
              </li>
            ))}
          </ul>
        </span>
      ) : validation.valid ? (
        <span className="min-w-48 flex-1 text-muted-foreground">
          {t('taxPages.irn.notRegistered')}
        </span>
      ) : (
        <span className="min-w-48 flex-1">
          {t('taxPages.irn.notReady')}
          <ul className="list-disc ps-5 text-xs">
            {validation.errors.map((e) => (
              <li key={`${e.rule}-${e.line ?? 0}`}>
                {e.line ? `${t('taxPages.irn.line', { line: e.line })} ` : ''}
                {e.message} ({e.rule})
              </li>
            ))}
          </ul>
        </span>
      )}
      <Button variant="link" size="sm" className="px-1" onClick={download}>
        <Download className="size-4" /> {t('taxPages.gst.downloadJson')}
      </Button>
      {staff && !irn ? (
        <Button
          size="sm"
          disabled={!validation.valid || action.isPending}
          onClick={() =>
            action.mutate(
              { kind: 'generate' },
              {
                onSuccess: (r) =>
                  toast(
                    t('taxPages.irn.toastGenerated', {
                      number: number ? ltrIn(number) : t('taxPages.irn.theDocument'),
                      ackNo: ltrIn(r.ackNo),
                    }),
                  ),
                onError: (e) => toast(e.message, { tone: 'error' }),
              },
            )
          }
        >
          {cancelled ? t('taxPages.irn.generateNew') : t('taxPages.irn.generate')}
        </Button>
      ) : null}
      {staff && irn && within24h ? (
        <Button size="sm" variant="outline" onClick={() => setCancelling(true)}>
          {t('taxPages.irn.cancel')}
        </Button>
      ) : null}
      <Dialog open={cancelling} onOpenChange={setCancelling}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('taxPages.irn.cancel')}</DialogTitle>
            <DialogDescription>{t('taxPages.irn.cancelHint')}</DialogDescription>
          </DialogHeader>
          <Field label={t('taxPages.ewayBill.reason')}>
            {(id) => (
              <NativeSelect
                id={id}
                value={String(reasonCode)}
                onChange={(e) => setReasonCode(Number(e.target.value))}
              >
                {IRN_CANCEL_REASONS.map(([v]) => (
                  <option key={v} value={v}>
                    {irnCancelReasonLabel(v)}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label={t('taxPages.ewayBill.remark')}>
            {(id) => (
              <Input
                id={id}
                value={reason}
                maxLength={100}
                placeholder={t('taxPages.irn.remarkPlaceholder')}
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelling(false)}>
              {t('taxPages.ewayBill.keepIt')}
            </Button>
            <Button
              disabled={reason.trim().length < 3 || action.isPending}
              onClick={() =>
                action.mutate(
                  { kind: 'cancel', reasonCode, remark: reason.trim() },
                  {
                    onSuccess: () => {
                      toast(t('taxPages.irn.toastCancelled'));
                      setCancelling(false);
                    },
                    onError: (e) => toast(e.message, { tone: 'error' }),
                  },
                )
              }
            >
              {t('taxPages.irn.cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
